/**
 * Which module the reader is in, kept steady across the screens the two share.
 *
 * **The bug this exists to fix**: somebody working in the development rail pressed Payments and
 * the whole navigation flipped to letting underneath them. Clients, payments, reports, bank
 * accounts, settings and support belong to *both* modules, so a path alone cannot say which one
 * somebody is in.
 *
 * Three sources, in order:
 *
 * 1. **What the path owns.** `/app/development` is development, `/app/units` is letting.
 *    Unarguable.
 * 2. **`?module=` in the address**, which the rail puts on every shared link it draws. Explicit,
 *    shareable, and survives a reload.
 * 3. **What they were in a moment ago.** The parameter is lost the instant somebody clicks a row
 *    *inside* a shared screen — `/app/payments/abc` has no module in it and never will, because
 *    that link is drawn by a table five components away. Threading the parameter through every
 *    link on every shared screen is a rule that will be forgotten on one of them; remembering it
 *    is not.
 *
 * `sessionStorage` rather than `localStorage`: it is per tab, so somebody with letting open in one
 * tab and development in another does not have the two fight over a single value. It is wrapped
 * because a browser in private mode throws on access rather than returning null.
 */

import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { isModule, moduleForPath, normaliseModules, withModule, type ModuleCode } from "@rentorng/core";
import { setRequestModule } from "./api";

const KEY = "rentorng.module";

function remembered(): ModuleCode | null {
  try {
    const value = window.sessionStorage.getItem(KEY);
    return isModule(value) ? value : null;
  } catch {
    return null;
  }
}

function remember(value: ModuleCode): void {
  try {
    window.sessionStorage.setItem(KEY, value);
  } catch {
    /* Private mode. The rail still works from the path and the parameter; it just forgets when
       somebody walks into the middle of a shared screen — a far better failure than not drawing. */
  }
}

/** The module whose rail should be showing, and which every shared link should carry. */
export function useActiveModule(modules: ModuleCode[]): ModuleCode {
  const location = useLocation();
  const held = normaliseModules(modules);

  const owned = moduleForPath(location.pathname);
  const askedRaw = new URLSearchParams(location.search).get("module");
  const asked = isModule(askedRaw) ? askedRaw : null;

  const candidate = owned ?? asked ?? remembered();
  const active = candidate && held.includes(candidate) ? candidate : held[0];

  /*
    Remember it only when something actually said so.

    Writing on every render would pin the fallback in place: land on `/app/payments` with nothing
    remembered, fall back to letting, store letting — and the answer is now letting for ever, which
    is the original bug with a longer memory.
  */
  useEffect(() => {
    if (owned || asked) remember(active);
  }, [owned, asked, active]);

  /*
    Tell the API client, so the shared lists come back scoped to the same module the rail is
    showing. Set synchronously rather than in an effect: a query fired during this render would
    otherwise go out carrying the *previous* module, and the first list after a switch would be
    the wrong one.
  */
  setRequestModule(active);

  return active;
}

/**
 * A link maker that keeps the reader inside the module they are in.
 *
 * **What this is for, and what it is not.** `withModule` leaves alone anything one module owns and
 * anything an organisation with a single module opens; what it decorates is a destination *both*
 * modules share. The rail, the tab bar and the create sheet already do this by hand. This hook is
 * for the rest of the **chrome** — the account menu, the plan banner, the padlock's modal, the
 * quick links on Settings — which is drawn on every screen in both modules and therefore has
 * exactly the same problem.
 *
 * **It is deliberately not extended to the links inside a table.** `/app/payments/abc` is drawn by
 * a component five levels down from anything that knows which module is in force, and threading a
 * parameter through every one of them is the rule that gets forgotten on the one screen nobody
 * checked. Those are covered by the remembering above, which is what it exists for. The rule is:
 * **navigation drawn on every screen carries the module; a row in a list does not have to.**
 */
export function useModuleLink(modules: ModuleCode[]): (to: string) => string {
  const held = normaliseModules(modules);
  const active = useActiveModule(held);
  return (to: string) => withModule(to, active, held);
}
